// What the editor's preview plays (spec editor/proxy-playback.md): each library video's 540p copy,
// or its original once this browser chose "Original quality", and the words of the pill that says
// so. Download and Save never come here: they resolve the project from its own media, the originals.
import type { SourceMedia } from "./media";

/** This browser's choice (`previewQuality` in `platform/prefs.ts`): the copies by default. */
export type PreviewQuality = "copies" | "original";

/** The pill's menu, in card 23's words and order. */
export const QUALITIES: readonly { value: PreviewQuality; label: string }[] = [
  { value: "copies", label: "540p copies, smooth" },
  { value: "original", label: "Original quality" },
];

/** Under the menu, and last in the tooltip. */
export const ORIGINALS_NOTE = "Download and Save use the original files.";

/** Why a video plays its original while copies are chosen: no copy will come for it. */
export const KEPT_WHY = "light already, longer than 15 minutes, or too large for a copy";

/** The media the preview plays: while copies are chosen, a video with a copy plays the copy. */
export function playedMedia(
  media: ReadonlyMap<string, SourceMedia>,
  quality: PreviewQuality,
): ReadonlyMap<string, SourceMedia> {
  if (quality === "original") return media;
  let played: Map<string, SourceMedia> | null = null;
  for (const [key, each] of media) {
    if (each.kind !== "video" || each.proxy === null) continue;
    played ??= new Map(media);
    played.set(key, { ...each, url: each.proxy });
  }
  return played ?? media;
}

export interface QualityWords {
  /** The pill's words: card 23's three states. */
  label: string;
  /** Its tooltip, a line each: what plays, counted, then `ORIGINALS_NOTE`. */
  details: readonly string[];
}

const videos = (count: number) => (count === 1 ? "1 video" : `${count} videos`);

/**
 * The pill's words for the project's library videos (the only files with copies), or null when it
 * has none and the pill is not shown. On its way while any copy is being made (those clips play
 * their originals meanwhile), a 540p preview while any video plays its copy, Original otherwise.
 */
export function qualityWords(media: ReadonlyMap<string, SourceMedia>, quality: PreviewQuality): QualityWords | null {
  const library = [...media.values()].filter((each) => each.kind === "video" && each.proxyStatus !== null);
  if (library.length === 0) return null;
  if (quality === "original") {
    return { label: "Original", details: ["The preview plays the original files.", ORIGINALS_NOTE] };
  }
  const copies = library.filter((each) => each.proxy !== null).length;
  const coming = library.filter((each) => each.proxyStatus === "pending").length;
  const kept = library.filter((each) => each.proxyStatus === "none").length;
  const label = coming > 0 ? "Original, 540p on its way" : copies > 0 ? "540p preview" : "Original";
  const details = [
    ...(copies > 0 ? [`540p copies: ${copies} of ${videos(library.length)}`] : []),
    ...(coming > 0 ? [`Copies on their way: ${coming}, playing the originals until then`] : []),
    ...(kept > 0 ? [`Original files: ${kept} (${KEPT_WHY})`] : []),
    ORIGINALS_NOTE,
  ];
  return { label, details };
}
